import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';
import './NotificationQueue.css';

interface PendingNotification {
  notification_id: string;
  user_id: string;
  notification_type: string;
  channel: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  queue_time_seconds: number;
}

interface QueueStats {
  by_channel: Array<{
    channel: string;
    pending_count: number;
    oldest_queue_time_seconds: number;
    avg_queue_time_seconds: number;
  }>;
  by_type: Array<{
    notification_type: string;
    pending_count: number;
  }>;
  oldest: {
    notification_id: string;
    notification_type: string;
    channel: string;
    created_at: string;
    queue_time_seconds: number;
  } | null;
  processing_rate: number;
}

export const NotificationQueue: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<PendingNotification[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [notificationDetails, setNotificationDetails] = useState<PendingNotification | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchQueue();
    fetchStats();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchQueue();
        fetchStats();
      }, 10000); // Refresh every 10 seconds

      return () => clearInterval(interval);
    }
  }, [selectedChannel, autoRefresh]);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const channelParam = selectedChannel !== 'all' ? `&channel=${selectedChannel}` : '';

      const response = await fetch(
        `${API_URL}/notification-queue/pending?limit=100${channelParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = await response.json();
      if (result.success && result.data) {
        setNotifications(result.data.notifications);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load notification queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notification-queue/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (err: any) {
      console.error('Failed to load queue stats:', err);
    }
  };

  const handleCancelNotification = async (notificationId: string) => {
    if (!window.confirm('Are you sure you want to cancel this notification?')) {
      return;
    }

    try {
      setActionLoading(`cancel-${notificationId}`);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_URL}/notification-queue/notifications/${notificationId}/cancel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Notification cancelled successfully');
        fetchQueue();
        fetchStats();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to cancel notification');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel notification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkCancel = async () => {
    if (selectedNotifications.size === 0) {
      setError('Please select notifications to cancel');
      return;
    }

    if (!window.confirm(`Cancel ${selectedNotifications.size} selected notifications?`)) {
      return;
    }

    try {
      setActionLoading('bulk-cancel');
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/notification-queue/bulk-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          notificationIds: Array.from(selectedNotifications),
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSuccessMessage(result.message);
        setSelectedNotifications(new Set());
        fetchQueue();
        fetchStats();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to cancel notifications');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel notifications');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePurgeOld = async () => {
    const days = prompt('Delete notifications older than how many days? (minimum 30)');
    if (!days) return;

    const daysNum = parseInt(days);
    if (isNaN(daysNum) || daysNum < 30) {
      setError('Please enter a number greater than or equal to 30');
      return;
    }

    if (!window.confirm(`This will permanently delete all sent/failed notifications older than ${daysNum} days. Continue?`)) {
      return;
    }

    try {
      setActionLoading('purge');
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/notification-queue/purge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ days: daysNum, status: 'all' }),
      });

      const result = await response.json();
      if (result.success) {
        setSuccessMessage(result.message);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to purge notifications');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to purge notifications');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewDetails = async (notification: PendingNotification) => {
    setNotificationDetails(notification);
  };

  const toggleSelection = (notificationId: string) => {
    const newSelection = new Set(selectedNotifications);
    if (newSelection.has(notificationId)) {
      newSelection.delete(notificationId);
    } else {
      newSelection.add(notificationId);
    }
    setSelectedNotifications(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.notification_id)));
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return '📧';
      case 'sms':
        return '📱';
      case 'in_app':
        return '🔔';
      default:
        return '📬';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatQueueTime = (seconds: number) => {
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTotalPending = () => {
    return stats?.by_channel.reduce((sum, ch) => sum + ch.pending_count, 0) || 0;
  };

  if (loading && notifications.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="notification-queue">
      <div className="page-header">
        <div className="header-content">
          <h1>📬 Notification Queue Monitor</h1>
          <p className="header-subtitle">Monitor and manage pending notifications in real-time</p>
        </div>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-refresh (10s)</span>
          </label>
          <Button onClick={fetchQueue} variant="secondary" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </Button>
          <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>✓ {successMessage}</span>
        </div>
      )}

      {/* Queue Statistics */}
      {stats && (
        <div className="queue-stats-grid">
          <div className="stat-card total">
            <div className="stat-icon">📬</div>
            <div className="stat-content">
              <h3>Total in Queue</h3>
              <p className="stat-value">{getTotalPending()}</p>
              <p className="stat-sub">pending notifications</p>
            </div>
          </div>
          <div className="stat-card processing">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <h3>Processing Rate</h3>
              <p className="stat-value">{stats.processing_rate}</p>
              <p className="stat-sub">processed last hour</p>
            </div>
          </div>
          {stats.oldest && (
            <div className="stat-card oldest">
              <div className="stat-icon">⏰</div>
              <div className="stat-content">
                <h3>Oldest in Queue</h3>
                <p className="stat-value">{formatQueueTime(stats.oldest.queue_time_seconds)}</p>
                <p className="stat-sub">{getNotificationTypeLabel(stats.oldest.notification_type)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Channel Breakdown */}
      {stats && stats.by_channel.length > 0 && (
        <div className="channel-breakdown">
          <h2>Queue by Channel</h2>
          <div className="channel-stats-grid">
            {stats.by_channel.map((channel) => (
              <div key={channel.channel} className="channel-stat-card">
                <span className="channel-icon">{getChannelIcon(channel.channel)}</span>
                <div className="channel-info">
                  <h4>{channel.channel.toUpperCase()}</h4>
                  <p className="pending-count">{channel.pending_count} pending</p>
                  <p className="avg-time">Avg wait: {formatQueueTime(channel.avg_queue_time_seconds)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="actions-bar">
        <div className="filter-section">
          <label>Filter by channel:</label>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="channel-filter"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="in_app">In-App</option>
          </select>
        </div>
        <div className="bulk-actions">
          {selectedNotifications.size > 0 && (
            <>
              <span className="selection-count">{selectedNotifications.size} selected</span>
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkCancel}
                loading={actionLoading === 'bulk-cancel'}
                disabled={!!actionLoading}
              >
                Cancel Selected
              </Button>
            </>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePurgeOld}
            loading={actionLoading === 'purge'}
            disabled={!!actionLoading}
          >
            Purge Old Notifications
          </Button>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="queue-table-section">
        {notifications.length === 0 ? (
          <div className="empty-queue">
            <div className="empty-icon">✅</div>
            <h3>Queue is Empty</h3>
            <p>No pending notifications at the moment</p>
          </div>
        ) : (
          <div className="queue-table">
            <table>
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.size === notifications.length && notifications.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Queue Time</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Subject</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.notification_id}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification.notification_id)}
                        onChange={() => toggleSelection(notification.notification_id)}
                      />
                    </td>
                    <td>
                      <span className={`queue-time ${notification.queue_time_seconds > 300 ? 'warning' : ''}`}>
                        {formatQueueTime(notification.queue_time_seconds)}
                      </span>
                    </td>
                    <td>
                      <div className="user-cell">
                        <div className="user-name">
                          {notification.first_name} {notification.last_name}
                        </div>
                        <div className="user-email">{notification.email}</div>
                      </div>
                    </td>
                    <td>{getNotificationTypeLabel(notification.notification_type)}</td>
                    <td>
                      <span className="channel-badge">
                        {getChannelIcon(notification.channel)} {notification.channel}
                      </span>
                    </td>
                    <td className="subject-cell" title={notification.subject}>
                      {notification.subject.substring(0, 50)}
                      {notification.subject.length > 50 && '...'}
                    </td>
                    <td>{formatDateTime(notification.created_at)}</td>
                    <td className="actions-cell">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(notification)}
                      >
                        View
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleCancelNotification(notification.notification_id)}
                        loading={actionLoading === `cancel-${notification.notification_id}`}
                        disabled={!!actionLoading}
                      >
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notification Details Modal */}
      {notificationDetails && (
        <div className="modal-overlay" onClick={() => setNotificationDetails(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Notification Details</h3>
              <button className="close-button" onClick={() => setNotificationDetails(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span className="detail-value">{getNotificationTypeLabel(notificationDetails.notification_type)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Channel:</span>
                <span className="detail-value">
                  {getChannelIcon(notificationDetails.channel)} {notificationDetails.channel}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">User:</span>
                <span className="detail-value">
                  {notificationDetails.first_name} {notificationDetails.last_name} ({notificationDetails.email})
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Queue Time:</span>
                <span className="detail-value">{formatQueueTime(notificationDetails.queue_time_seconds)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{formatDateTime(notificationDetails.created_at)}</span>
              </div>
              <div className="detail-section">
                <span className="detail-label">Subject:</span>
                <div className="detail-text">{notificationDetails.subject}</div>
              </div>
              <div className="detail-section">
                <span className="detail-label">Message:</span>
                <div className="detail-text">{notificationDetails.message}</div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setNotificationDetails(null)}>
                Close
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  handleCancelNotification(notificationDetails.notification_id);
                  setNotificationDetails(null);
                }}
              >
                Cancel Notification
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
